/**
 * Fuel, charge and money: what every machine runs on, how much it holds and
 * burns, and what filling it up costs, in Swedish kronor.
 *
 * Prices are Sweden's, autumn 2026, at the pump: petrol (95) and diesel a
 * little under twenty kronor a litre, a public fast charger six-ish a kWh,
 * home electricity two and a half, Jet A-1 at a helipad's tank about
 * twenty-five a litre. Consumption is real too — a hatchback does 5.8 l per
 * 100 km — so a half-full tank goes a long way, as it does.
 *
 * Only the filling is faster than life: a pump's 35 l a minute is kept near
 * enough, but a charger's hour is cut to a minute or two, which nobody wants
 * to sit through in a game. The price per kWh is the real one.
 *
 * Pure: no DOM, no storage. The wallet's persistence is the caller's.
 */

export type Energy = 'petrol' | 'diesel' | 'electric' | 'jet';

/** Machines that carry energy: each car body, the motorbike, the e-scooter and the helicopter. */
export type Machine = 'hatch' | 'sedan' | 'estate' | 'suv' | 'van' | 'pickup' | 'truck' | 'semi' | 'bus' | 'moto' | 'scooter' | 'heli';

/** Where energy is bought. */
export type Source = 'fuel' | 'charger' | 'home' | 'helipad';

/** Somewhere energy is sold: a forecourt, a charging point, a house's driveway, in chunk metres. */
export interface EnergySpot {
  x: number;
  z: number;
  /** How near its middle a stopped vehicle must be, metres. */
  radius: number;
  source: Source;
  /** A station's brand or name, when it has one. */
  name: string;
}

export interface Tank {
  energy: Energy;
  /** Litres, or kWh for a battery. */
  capacity: number;
  level: number;
  /** Use per 100 km on the road, in its unit; for the helicopter, per hour flying. */
  use: number;
}

interface Spec { litres: number; petrol: number; diesel: number; kwh: number; electric: number }

/** Tank and battery sizes, and use per 100 km, per car body. */
const CARS: Record<Exclude<Machine, 'moto' | 'scooter' | 'heli' | 'truck' | 'semi' | 'bus'>, Spec> = {
  hatch: { litres: 45, petrol: 5.8, diesel: 4.8, kwh: 52, electric: 15.5 },
  sedan: { litres: 55, petrol: 6.4, diesel: 5.2, kwh: 75, electric: 16.5 },
  estate: { litres: 60, petrol: 6.8, diesel: 5.5, kwh: 77, electric: 18 },
  suv: { litres: 65, petrol: 8.2, diesel: 6.8, kwh: 85, electric: 21 },
  van: { litres: 70, petrol: 9.5, diesel: 8.2, kwh: 75, electric: 25 },
  pickup: { litres: 80, petrol: 11.5, diesel: 8.8, kwh: 98, electric: 27 },
};

/**
 * Trucks run on diesel: a laden box truck about 24 l per 100 km from a 250 l
 * tank, an articulated truck at forty tonnes about 32 from 700 l.
 */
const TRUCKS = { truck: { litres: 250, diesel: 24 }, semi: { litres: 700, diesel: 32 } } as const;

/** Motorbike: a 15 l tank at 4.5 l per 100 km. */
const MOTO_TANK = 15;
const MOTO_USE = 4.5;
/** E-scooter: a KuKirin G2's 48 V 15 Ah battery, 0.72 kWh, good for some 55 km. */
const SCOOTER_BATTERY = 0.72;
const SCOOTER_USE = 1.3;
/** Helicopter: Jet A-1, a light turbine's tank and its burn per hour aloft. */
export const HELI_TANK = 240;
const HELI_BURN = 95;
/** Burn with the rotor turning on the ground, per hour. */
const HELI_IDLE = 35;

/** Kronor per litre or kWh, by source and energy. */
export const PRICE = {
  petrol: 18.89,
  diesel: 19.79,
  /** A public charger, fast or slow: one price. */
  charger: 5.95,
  /** Electricity at home, with its network fees. */
  home: 2.45,
  jet: 24.9,
} as const;

/** How fast a tank fills, units per second: a pump, a fast charger, a wallbox, the pad's bowser. */
const RATE: Record<Source, number> = { fuel: 0.7, charger: 1.1, home: 0.35, helipad: 1.6 };

/** The money a player starts with, kronor. */
export const START_BALANCE = 3000;

/** A number 0..1 from a seed: which energy a car runs on, and how full it is, stay the car's own. */
function unit(seed: number, salt: number): number {
  let h = Math.imul(seed ^ salt, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

/**
 * A machine's tank as it is found: petrol, diesel or electric by body — vans
 * and pickups mostly diesel, and about a third of the rest electric, as
 * Swedish cars now are — somewhere around half full.
 */
export function foundTank(machine: Machine, seed: number): Tank {
  const level = 0.25 + 0.5 * (unit(seed, 0x51) + unit(seed, 0x77)) / 2 + (unit(seed, 0x13) - 0.5) * 0.2;
  if (machine === 'heli') return { energy: 'jet', capacity: HELI_TANK, level: HELI_TANK * level, use: HELI_BURN };
  if (machine === 'moto') return { energy: 'petrol', capacity: MOTO_TANK, level: MOTO_TANK * level, use: MOTO_USE };
  if (machine === 'scooter') return { energy: 'electric', capacity: SCOOTER_BATTERY, level: SCOOTER_BATTERY * level, use: SCOOTER_USE };
  // A town bus runs on its battery, as most of Västtrafik's in Gothenburg do:
  // about 400 kWh, at some 110 kWh per 100 km.
  if (machine === 'bus') return { energy: 'electric', capacity: 400, level: 400 * level, use: 110 };
  if (machine === 'truck' || machine === 'semi') {
    const t = TRUCKS[machine];
    return { energy: 'diesel', capacity: t.litres, level: t.litres * level, use: t.diesel };
  }
  const spec = CARS[machine];
  const u = unit(seed, 0x2a);
  const workhorse = machine === 'van' || machine === 'pickup';
  const energy: Energy = u < (workhorse ? 0.12 : 0.32) ? 'electric' : u < (workhorse ? 0.8 : 0.58) ? 'diesel' : 'petrol';
  if (energy === 'electric') return { energy, capacity: spec.kwh, level: spec.kwh * level, use: spec.electric };
  return { energy, capacity: spec.litres, level: spec.litres * level, use: energy === 'diesel' ? spec.diesel : spec.petrol };
}

/** Drive `metres` on the road: the tank's level after it. */
export function drive(tank: Tank, metres: number): void {
  tank.level = Math.max(0, tank.level - (tank.use * metres) / 100_000);
}

/** Run the helicopter for `seconds`, aloft or turning over on the ground. */
export function fly(tank: Tank, seconds: number, aloft: boolean): void {
  tank.level = Math.max(0, tank.level - ((aloft ? HELI_BURN : HELI_IDLE) * seconds) / 3600);
}

/** How far the tank goes on the road, km. */
export function rangeKm(tank: Tank): number {
  return tank.use > 0 ? (tank.level / tank.use) * 100 : 0;
}

/** Whether `source` can fill a tank of `energy`. */
export function canFill(energy: Energy, source: Source): boolean {
  if (energy === 'jet') return source === 'helipad';
  if (energy === 'electric') return source === 'charger' || source === 'home';
  return source === 'fuel';
}

/** Kronor a unit, for this energy from this source. */
export function priceOf(energy: Energy, source: Source): number {
  if (energy === 'jet') return PRICE.jet;
  if (energy === 'electric') return source === 'home' ? PRICE.home : PRICE.charger;
  return energy === 'diesel' ? PRICE.diesel : PRICE.petrol;
}

/**
 * Fill for `seconds` from `source`, paying from `balance`: as much as the
 * rate, the room in the tank and the money allow. Returns what went in and
 * what it cost, in kronor.
 */
export function fill(tank: Tank, source: Source, seconds: number, balance: number): { added: number; cost: number } {
  if (!canFill(tank.energy, source)) return { added: 0, cost: 0 };
  const price = priceOf(tank.energy, source);
  const added = Math.max(0, Math.min(RATE[source] * seconds, tank.capacity - tank.level, balance / price));
  tank.level += added;
  return { added, cost: added * price };
}

/** A unit's name, for a price: "l" or "kWh". */
export function unitOf(energy: Energy): string {
  return energy === 'electric' ? 'kWh' : 'l';
}

/** Kronor as a Swede writes them: "1 234 kr", "18,89 kr". */
export function kronor(amount: number, decimals = 0): string {
  const fixed = Math.abs(amount).toFixed(decimals);
  const [whole, frac] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${amount < 0 ? '−' : ''}${grouped}${frac !== undefined ? `,${frac}` : ''} kr`;
}
